/* eslint-disable react/no-multi-comp -- pre-existing: this file has always
   declared its sub-components inline. `lint:changed` only lints files a branch
   touches, so the violation surfaced when BON-528 edited one line here. The
   split into one-component-per-file wants its own change, where it can be
   reviewed on its merits. */
import { mockedLastActive } from '../mockedFields';
import type { UserRow } from '../userViewModel';

import MockedValue from './MockedValue';
import UserAvatar from './UserAvatar';
import UserRegionChips from './UserRegionChips';
import UserRoleChips from './UserRoleChips';
import UserRowActions from './UserRowActions';
import UserStatusPills from './UserStatusPills';

interface UsersTableProps {
  rows: UserRow[];
  selectedId?: string | null;
  onSelect: (row: UserRow) => void;
  /** Open the Invite drawer pre-filled from this user. Wired only on
   *  imported rows — the per-row "Send invite" button calls it. */
  onInvite?: (row: UserRow) => void;
  /** Per-row actions on invited rows. */
  onResendInvite?: (row: UserRow) => void;
  onRevokeInvite?: (row: UserRow) => void;
  /** Region ids the viewer can manage; others render locked. Null = none locked. */
  manageableRegionIds?: string[] | null;
}

type RowActionHandlers = Pick<UsersTableProps, 'onInvite' | 'onResendInvite' | 'onRevokeInvite'>;

const COLS = ['User', 'Role(s)', 'Region(s)', 'Status', 'Last active', ''];

const th =
  'text-left py-2.5 text-[10px] font-semibold tracking-[.08em] uppercase text-stone-500 dark:text-stone-400 border-b border-stone-200 dark:border-stone-800';
const td = 'py-2.5 px-3 border-b border-stone-200 dark:border-stone-800 align-middle';

function Row({
  u,
  selected,
  onSelect,
  actions,
  manageableRegionIds,
}: {
  u: UserRow;
  selected: boolean;
  onSelect: (row: UserRow) => void;
  actions: RowActionHandlers;
  manageableRegionIds?: string[] | null;
}) {
  // Selected-row tokens per SelectedUserRow.handoff.md:
  //   fill   = --um-primary-soft : #f0faf4 (light) / #11281c (dark)
  //   bar    = --um-primary      : #0e874b (light) / #38c97e (dark)
  //   hover  = --um-surface      : #f7f8f7 (light) / #1e2520 (dark)
  // The accent bar lives on the leading <td> (not <tr>) because the table
  // uses border-collapse: collapse — <tr> borders are unreliable under
  // collapse. Unselected rows reserve the bar's 3px with a transparent
  // border so selection never jogs the layout sideways.
  return (
    <tr
      onClick={() => onSelect(u)}
      aria-selected={selected}
      className={`cursor-pointer transition ${
        selected ? 'bg-[#f0faf4] dark:bg-[#11281c]' : 'hover:bg-[#f7f8f7] dark:hover:bg-[#1e2520]'
      }`}
    >
      <td
        className={`py-2.5 pl-[18px] border-b border-stone-200 dark:border-stone-800 align-middle border-l-[3px] ${
          selected ? 'border-l-[#0e874b] dark:border-l-[#38c97e]' : 'border-l-transparent'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <UserAvatar name={u.name} size={28} />
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-bark-900 dark:text-[#e8ece9]">
              {u.name}
            </div>
            <div className="text-[11px] text-stone-500 dark:text-stone-400">
              {u.email || (
                <span className="font-mono text-[10.5px] text-[#BA7517]">
                  Imported · {u.fareharborUsername || 'no email'}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className={td}>
        <UserRoleChips roles={u.roles} max={2} />
      </td>
      <td className={td}>
        <UserRegionChips regions={u.regions} manageableRegionIds={manageableRegionIds} max={3} />
      </td>
      <td className={td}>
        <div className="flex flex-wrap gap-1">
          <UserStatusPills user={u} />
        </div>
      </td>
      <td className={`${td} text-[11.5px] tabular-nums whitespace-nowrap`}>
        <MockedValue pending="UserSession tracking / Auth0 last_login">
          {mockedLastActive(u.id)}
        </MockedValue>
      </td>
      <UserRowActions user={u} onSelect={onSelect} {...actions} />
    </tr>
  );
}

/** Scrollable users table. */
export default function UsersTable({
  rows,
  selectedId,
  onSelect,
  onInvite,
  onResendInvite,
  onRevokeInvite,
  manageableRegionIds = null,
}: UsersTableProps) {
  const actions: RowActionHandlers = { onInvite, onResendInvite, onRevokeInvite };
  return (
    <div className="flex-1 min-h-0 overflow-auto">
      <table className="w-full border-collapse text-[13px]">
        <thead className="sticky top-0 bg-white dark:bg-[#1a1f1c] z-[1]">
          <tr>
            {COLS.map((h, i) => (
              <th
                key={i}
                className={th}
                style={{
                  paddingLeft: i === 0 ? 18 : 12,
                  paddingRight: i === COLS.length - 1 ? 18 : 12,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <Row
              key={u.id}
              u={u}
              selected={u.id === selectedId}
              onSelect={onSelect}
              actions={actions}
              manageableRegionIds={manageableRegionIds}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* eslint-enable react/no-multi-comp -- pairs with file-top disable */
